/* Product-type pills on homepage featured-collection sliders — snippets/uoy-type-tabs.liquid.
   A type pill fetches that collection filtered by product type (sections/uoy-type-products.liquid via the
   Section Rendering API) and swaps the slider cells; "All" puts the original cells back.
   Speed: after the page has fully loaded, the pills of a section near the screen fetch their products in the
   background (one low-priority request at a time), so a click usually swaps instantly; swapped-in images are
   unveiled directly because the theme's lazy loader does not pick up cells appended to a slider. */
(() => {
  if (window.uoyTypeTabs) return;
  window.uoyTypeTabs = true;

  const fetched = new Map(); // url -> Promise<Element[]>
  const originals = new WeakMap(); // product list -> original cell elements

  const filterQuery = (types) => types.map((t) => 'filter.p.product_type=' + encodeURIComponent(t)).join('&');

  function load(url, options) {
    if (!fetched.has(url)) {
      fetched.set(url, fetch(url, options)
        .then((res) => {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        })
        .then((html) => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.uoy-tp-items > .t4s-product')])
        .catch((err) => {
          fetched.delete(url);
          throw err;
        }));
    }
    return fetched.get(url);
  }

  const tabUrl = (bar, types) => bar.dataset.url + '?section_id=' + bar.dataset.section + '&' + filterQuery(types);
  const pillTypes = (pill) => JSON.parse(pill.dataset.types || '[]');

  // load the product image of each cell now (lazySizesT4 = the theme's lazy loader)
  function unveil(cells) {
    const lazy = window.lazySizesT4;
    if (!lazy) return;
    cells.forEach((cell) => {
      const img = cell.querySelector('img.t4s-product-main-img.lazyloadt4s');
      if (img) lazy.loader.unveil(img);
    });
  }

  // start downloading the images a tab will show first, using the URL the lazy loader will request
  // (data-src with width= the smallest data-widths entry covering the card at this screen's pixel density)
  const warmed = new WeakSet();
  function warm(list, cells) {
    const sample = list.querySelector('.t4s-product');
    const sampleImg = sample && sample.querySelector('img.t4s-product-main-img');
    const cellWidth = sample ? sample.getBoundingClientRect().width : 0;
    const imgWidth = sampleImg ? sampleImg.getBoundingClientRect().width : 0;
    if (!cellWidth || !imgWidth) return;
    const perView = Math.max(1, Math.round(list.getBoundingClientRect().width / cellWidth));
    const need = imgWidth * (window.devicePixelRatio || 1);
    cells.slice(0, perView).forEach((cell) => {
      const img = cell.querySelector('img.t4s-product-main-img[data-src]');
      if (!img || warmed.has(img)) return;
      warmed.add(img);
      const widths = JSON.parse(img.dataset.widths || '[]');
      const width = widths.find((w) => w >= need) || widths[widths.length - 1];
      if (width) new Image().src = img.dataset.src.replace(/([?&]width=)\d+/, '$1' + width);
    });
  }

  function swap(list, cells) {
    const flkty = window.Flickityt4s && window.Flickityt4s.data(list);
    if (flkty) {
      flkty.remove(flkty.getCellElements());
      flkty.append(cells);
      flkty.select(0, false, true);
      unveil(flkty.selectedElements);
      if (!list.dataset.uoyTtUnveil) {
        list.dataset.uoyTtUnveil = '1';
        flkty.on('select', () => unveil(flkty.selectedElements));
      }
    } else {
      list.replaceChildren(...cells);
      unveil(cells.filter((cell) => cell.getBoundingClientRect().top < window.innerHeight * 1.5));
    }
    // same refresh the theme runs after injecting product HTML (T4SThemeSP.RenderRefresh)
    if (window.jQuery_T4NT) window.jQuery_T4NT(document).trigger('currency:update');
    if (window.T4SThemeSP) {
      if (typeof T4SThemeSP.reinitProductGridItem === 'function') T4SThemeSP.reinitProductGridItem();
      if (T4SThemeSP.ProductItem && typeof T4SThemeSP.ProductItem.resizeObserver === 'function') T4SThemeSP.ProductItem.resizeObserver();
    }
  }

  // which ends of a scrollable pill row still hide pills (drives the edge fade in uoy-type-tabs.css)
  function edges(bar) {
    const max = bar.scrollWidth - bar.clientWidth;
    bar.classList.toggle('has-more-left', bar.scrollLeft > 2);
    bar.classList.toggle('has-more-right', bar.scrollLeft < max - 2);
  }
  function initBars() {
    document.querySelectorAll('[data-uoy-tt-bar]').forEach((bar) => {
      if (!bar.dataset.uoyTtInit) {
        bar.dataset.uoyTtInit = '1';
        bar.addEventListener('scroll', () => edges(bar), { passive: true });
      }
      edges(bar);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initBars);
  else initBars();
  window.addEventListener('resize', initBars);

  // background prefetch: only after the page's own load, only for sections near the screen, one at a time
  function prefetch(bar) {
    [...bar.querySelectorAll('[data-uoy-tt]')]
      .map(pillTypes)
      .filter((types) => types.length)
      .reduce((chain, types) => chain.then(() => load(tabUrl(bar, types), { priority: 'low' }).catch(() => {})), Promise.resolve());
  }
  function startPrefetch() {
    if (!('IntersectionObserver' in window)) return;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        idle(() => prefetch(entry.target));
      });
    }, { rootMargin: '400px 0px' });
    document.querySelectorAll('[data-uoy-tt-bar]').forEach((bar) => observer.observe(bar));
  }
  if (document.readyState === 'complete') startPrefetch();
  else window.addEventListener('load', startPrefetch);

  // a pill under the finger/mouse loads its products (if the prefetch has not yet) and their first images
  document.addEventListener('pointerover', (event) => {
    const pill = event.target.closest && event.target.closest('[data-uoy-tt]');
    const bar = pill && pill.closest('[data-uoy-tt-bar]');
    const list = bar && bar.closest('.t4s-section-inner') && bar.closest('.t4s-section-inner').querySelector('.t4s-products');
    const types = pill ? pillTypes(pill) : [];
    if (list && types.length) load(tabUrl(bar, types)).then((cells) => warm(list, cells)).catch(() => {});
  }, { passive: true });

  document.addEventListener('click', async (event) => {
    const pill = event.target.closest('[data-uoy-tt]');
    if (!pill || pill.classList.contains('is--active')) return;
    const bar = pill.closest('[data-uoy-tt-bar]');
    const section = bar && bar.closest('.t4s-section-inner');
    const list = section && section.querySelector('.t4s-products');
    if (!list) return;

    const types = pillTypes(pill);
    const filtered = types.length ? bar.dataset.url + '?' + filterQuery(types) : bar.dataset.url;
    if (!originals.has(list)) {
      const flkty = window.Flickityt4s && window.Flickityt4s.data(list);
      originals.set(list, flkty ? flkty.getCellElements() : [...list.children]);
    }

    bar.querySelectorAll('[data-uoy-tt]').forEach((p) => {
      p.classList.toggle('is--active', p === pill);
      p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
    });
    if (bar.scrollWidth > bar.clientWidth) {
      bar.scrollTo({ left: pill.offsetLeft - (bar.clientWidth - pill.offsetWidth) / 2, behavior: 'smooth' });
    }

    let cells = originals.get(list);
    if (types.length) {
      // dim the slider only if the products are not already prefetched
      const dim = setTimeout(() => list.classList.add('uoy-tt-loading'), 120);
      try {
        cells = await load(tabUrl(bar, types));
      } catch (err) {
        // request failed: the filtered collection page shows the same products
        window.location.href = filtered;
        return;
      } finally {
        clearTimeout(dim);
        list.classList.remove('uoy-tt-loading');
      }
      // a newer click already took over this slider
      if (!pill.classList.contains('is--active')) return;
      if (!cells.length) {
        // only unpublished products have this type: drop the pill and fall back to "All"
        const all = bar.querySelector('[data-uoy-tt][data-types="[]"]');
        pill.remove();
        if (all) all.click();
        return;
      }
    }
    swap(list, cells);

    const viewAll = section.querySelector('.t4s-viewall-btn');
    if (viewAll) viewAll.href = filtered;
  });
})();
